/**
 * The organization timezone of the daily Contact Owner change limit (#863, D4).
 *
 * Each person may make two manual Contact Owner changes a day; "a day" is this
 * timezone's day, else the person's profile timezone, else UTC. The setting
 * (`organizations.settings.timezone`) is read ONLY by the quota
 * (`services/contact_owner_quota.py`): booking links, campaigns, coach
 * schedules and how times display keep their own timezone settings.
 *
 * Self-contained like AiPrivacyControls: its own GET and PUT of
 * `/organizations/current`, so the Organization page change is one mount line.
 * The server validates the zone (422 `invalid_timezone`); `null` clears it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import TimezoneSelect from '../TimezoneSelect';
import { errorText } from '../../lib/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerQuotaTimezoneSetting() {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [zone, setZone] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/organizations/current`, { headers: getAuthHeader() });
            setZone(res.data?.settings?.timezone || '');
        } catch (err) {
            toast.error(errorText(err, 'Could not load the organization timezone'));
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const save = async (next) => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/organizations/current`, { settings: { timezone: next } },
                                        { headers: getAuthHeader() });
            setZone(res.data?.settings?.timezone || '');
            toast.success(next ? 'Organization timezone saved' : 'Organization timezone cleared');
        } catch (err) {
            toast.error(errorText(err, 'Could not save the organization timezone'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="py-2"><Loader2 className="w-4 h-4 animate-spin text-stone-400" /></div>;
    }
    return (
        <div className="space-y-2" data-testid="owner-quota-timezone">
            <label htmlFor="owner-quota-timezone" className="block text-sm font-medium text-stone-800">
                Organization timezone
            </label>
            <div className="flex items-center gap-2 flex-wrap">
                <TimezoneSelect id="owner-quota-timezone" value={zone} onChange={(z) => save(z)} disabled={saving}
                                placeholder="Not set" ariaLabel="Organization timezone" className="w-[320px] max-w-full" />
                {zone && (
                    <button type="button" onClick={() => save(null)} disabled={saving}
                            data-testid="owner-quota-timezone-clear"
                            className="h-9 px-3 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                        Clear
                    </button>
                )}
            </div>
            <p className="text-xs text-stone-500">
                Used for the daily limit on Contact Owner changes. Booking links, campaigns, coach schedules and how
                times display keep their own timezone settings.
            </p>
            {!zone && (
                <p className="text-xs text-stone-500" data-testid="owner-quota-timezone-unset">
                    Not set — each person&apos;s profile timezone applies, then UTC.
                </p>
            )}
        </div>
    );
}
